/**
 * AnchorManager - Manages anchor point visualization for connector creation
 * Shows anchor circles on shapes when hovered (for direct Konva approach)
 */

import Konva from 'konva';
import { EditableElement, AnchorPoint } from '../editing/types';
import { calculateAnchorPoints, isShapeElement } from '../utils/connectorUtils';

interface AnchorManagerOptions {
  anchorRadius?: number;
  anchorOffset?: number;
  anchorFill?: string;
  anchorHoverFill?: string;
  anchorStroke?: string;
  anchorStrokeWidth?: number;
}

const DEFAULT_OPTIONS: AnchorManagerOptions = {
  anchorRadius: 4,
  anchorOffset: 28, // 18px offset + 6px radius
  anchorFill: '#B3D9FF', // Light blue solid fill
  anchorHoverFill: '#0086c9', // Blue for hover
  anchorStroke: 'transparent', // No stroke for solid appearance
  anchorStrokeWidth: 0, // No stroke width
};

const HOVER_PROXIMITY_THRESHOLD = 25; // px
const ANCHOR_BLUE_COLOR = '#0086c9';
const ANCHOR_EXPANDED_RADIUS = 11; // Larger radius for state 1/3 and 2/4

type AnchorVisualState = 'default' | 'near' | 'hover'; // near = within 25px, hover = directly on anchor

interface AnchorState {
  anchor: AnchorPoint;
  group: Konva.Group;
  circle: Konva.Circle;
  iconGroup: Konva.Group | null;
  state: AnchorVisualState;
  previewGroup: Konva.Group | null;
}

export class AnchorManager {
  private layer: Konva.Layer;
  private options: Required<AnchorManagerOptions>;
  private anchorGroup: Konva.Group | null = null;
  private hoveredElementId: string | null = null;
  private selectedIds: string[] = [];
  private elements: EditableElement[] = [];
  private isTransforming: boolean = false; // Flag to hide anchors during drag/transform
  
  // Mouse tracking
  private currentMousePos: { x: number; y: number } | null = null;
  private anchorStates: Map<string, AnchorState> = new Map(); // anchor key -> state
  
  // Callbacks
  private onAnchorClick: ((anchor: AnchorPoint, element: EditableElement) => void) | null = null;
  private onAnchorDragStart: ((anchor: AnchorPoint, element: EditableElement) => void) | null = null;
  private onAnchorDragMove: ((anchor: AnchorPoint, element: EditableElement, pointerPos: { x: number; y: number }) => void) | null = null;
  private onAnchorDragEnd: ((anchor: AnchorPoint, element: EditableElement, pointerPos: { x: number; y: number }) => void) | null = null;
  
  // Mouse handlers references for cleanup
  private mouseMoveHandler: (() => void) | null = null;
  private mouseLeaveHandler: (() => void) | null = null;

  constructor(layer: Konva.Layer, options: AnchorManagerOptions = {}) {
    this.layer = layer;
    this.options = { ...DEFAULT_OPTIONS, ...options } as Required<AnchorManagerOptions>;
    // Setup mouse tracking will be done when anchors are first updated (stage will be available)
  }

  /**
   * Setup mouse tracking on stage (more reliable than layer)
   */
  private setupMouseTracking(): void {
    const stage = this.layer.getStage();

    if (!stage) return;
    
    this.mouseMoveHandler = () => {
      const currentStage = this.layer.getStage();
      if (!currentStage) return;
      
      const pointerPos = currentStage.getPointerPosition();
    
      if (!pointerPos) return;
      
      // Convert to layer coordinates using layer's absolute transform (accounts for viewport zoom/pan)
      const transform = this.layer.getAbsoluteTransform().copy();
      transform.invert();
      const layerPos = transform.point(pointerPos);
  
      
      this.currentMousePos = layerPos;
      this.updateAnchorStates();
    };
    
    this.mouseLeaveHandler = () => {
      // Reset mouse position when leaving stage
      this.currentMousePos = null;
      // Reset all anchors to default state
      this.anchorStates.forEach((anchorState) => {
        if (anchorState.state !== 'default') {
          anchorState.state = 'default';
          this.updateAnchorVisualState(anchorState, 'default');
        }
      });
      this.layer.batchDraw();
    };
    
    // Use stage events for more reliable tracking
    stage.on('mousemove', this.mouseMoveHandler);
    stage.on('mouseleave', this.mouseLeaveHandler);
 
  }

  /**
   * Set elements list
   */
  setElements(elements: EditableElement[]): void {
    this.elements = elements;
    this.updateAnchors();
  }

  /**
   * Set selected element IDs (anchors hidden when element is selected)
   */
  setSelectedIds(ids: string[]): void {
    this.selectedIds = ids;
    this.updateAnchors();
  }

  /**
   * Set hovered element ID
   */
  setHoveredElementId(id: string | null): void {
    if (this.hoveredElementId === id) return;
    this.hoveredElementId = id;
    this.updateAnchors();
  }

  /**
   * Get current hovered element ID
   */
  getHoveredElementId(): string | null {
    return this.hoveredElementId;
  }

  /**
   * Set anchor click callback
   */
  setOnAnchorClick(callback: (anchor: AnchorPoint, element: EditableElement) => void): void {
    this.onAnchorClick = callback;
  }

  /**
   * Set anchor drag callbacks
   */
  setOnAnchorDrag(
    onStart: (anchor: AnchorPoint, element: EditableElement) => void,
    onMove: (anchor: AnchorPoint, element: EditableElement, pointerPos: { x: number; y: number }) => void,
    onEnd: (anchor: AnchorPoint, element: EditableElement, pointerPos: { x: number; y: number }) => void
  ): void {
    this.onAnchorDragStart = onStart;
    this.onAnchorDragMove = onMove;
    this.onAnchorDragEnd = onEnd;
  }

  /**
   * Set transforming state (hides anchors during drag/transform)
   */
  setTransforming(isTransforming: boolean): void {
    this.isTransforming = isTransforming;
    this.updateAnchors();
  }

  /**
   * Calculate distance between two points
   */
  private distance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  }

  /**
   * Get current viewport scale from layer
   */
  private getViewportScale(): number {
    const scale = this.layer.scaleX();
    // Ensure scale is never zero or negative
    return scale > 0 ? scale : 1;
  }

  /**
   * Create plus icon group
   */
  private createPlusIcon(color: string): Konva.Group {
    const group = new Konva.Group();
    const baseSize = 10; // Base size in layer coordinates
    // Scale icon inversely with viewport so it maintains screen size
    // When zoomed out (scale 0.5), icon is 2x larger in layer coords = same screen size
    // When zoomed in (scale 2), icon is 0.5x in layer coords = same screen size
    const viewportScale = this.getViewportScale();
    const size = baseSize / viewportScale;
    const strokeWidth = (2.5 / viewportScale);
    
    // Horizontal line
    const hLine = new Konva.Line({
      points: [-size / 2, 0, size / 2, 0],
      stroke: color,
      strokeWidth: strokeWidth,
      lineCap: 'round',
      lineJoin: 'round',
    });
    
    // Vertical line
    const vLine = new Konva.Line({
      points: [0, -size / 2, 0, size / 2],
      stroke: color,
      strokeWidth: strokeWidth,
      lineCap: 'round',
      lineJoin: 'round',
    });
    
    group.add(hLine);
    group.add(vLine);
    return group;
  }

  /**
   * Create arrow icon group pointing in specified direction
   */
  private createArrowIcon(color: string, direction: 'top' | 'right' | 'bottom' | 'left' = 'right'): Konva.Group {
    const group = new Konva.Group();
    const baseSize = 14; // Base size in layer coordinates
    // Scale icon inversely with viewport so it maintains screen size
    const viewportScale = this.getViewportScale();
    const size = baseSize / viewportScale;
    const strokeWidth = 2.5 / viewportScale;
    const headSize = 4 / viewportScale; // Arrow head size
    
    let rotation = 0;
    switch (direction) {
      case 'top':
        rotation = -90;
        break;
      case 'right':
        rotation = 0;
        break;
      case 'bottom':
        rotation = 90;
        break;
      case 'left':
        rotation = 180;
        break;
    }
    
    // Arrow line (horizontal, will be rotated)
    const line = new Konva.Line({
      points: [-size / 2, 0, size / 2 - headSize, 0],
      stroke: color,
      strokeWidth: strokeWidth,
      lineCap: 'round',
      lineJoin: 'round',
    });
    
    // Arrow head (triangle pointing right, will be rotated)
    const arrowHeadOffset = 4 / viewportScale;
    const arrowHead = new Konva.Path({
      data: `M ${size / 2 - headSize} 0 L ${size / 2 - headSize - arrowHeadOffset} -${headSize} L ${size / 2 - headSize - arrowHeadOffset} ${headSize} Z`,
      fill: color,
    });
    
    group.add(line);
    group.add(arrowHead);
    
    // Rotate the entire group to point in the correct direction
    if (rotation !== 0) {
      group.rotation(rotation);
    }
    
    return group;
  }

  /**
   * Get anchor key for map
   */
  private getAnchorKey(side: string, elementId: string): string {
    return `${elementId}-${side}`;
  }

  /**
   * Update anchor states based on mouse position
   */
  private updateAnchorStates(): void {
  
    if (!this.currentMousePos || !this.anchorGroup) return;
    
    const mouseX = this.currentMousePos.x;
    const mouseY = this.currentMousePos.y;
    
    this.anchorStates.forEach((anchorState, key) => {
      const anchor = anchorState.anchor;
      const distance = this.distance(mouseX, mouseY, anchor.x, anchor.y);
      
      // Scale thresholds based on viewport scale (they should be in layer coordinates)
      const viewportScale = this.getViewportScale();
      const scaledExpandedRadius = ANCHOR_EXPANDED_RADIUS / viewportScale;
      const scaledProximityThreshold = HOVER_PROXIMITY_THRESHOLD / viewportScale;
      
      // Check if mouse is directly on anchor (within anchor radius)
      const isOnAnchor = distance <= scaledExpandedRadius;
      // Check if mouse is within proximity threshold
      const isNear = distance <= scaledProximityThreshold;
      
      let newState: AnchorVisualState = 'default';
      if (isOnAnchor) {
        newState = 'hover';
      } else if (isNear) {
        newState = 'near';
      }
      
      
      if (anchorState.state !== newState) {
        anchorState.state = newState;
       
        this.updateAnchorVisualState(anchorState, newState);
      }
    });
    
    this.layer.batchDraw();
  }

  /**
   * Update visual state of an anchor
   */
  private updateAnchorVisualState(anchorState: AnchorState, state: AnchorVisualState): void {
    const { circle, iconGroup, anchor, group, previewGroup } = anchorState;
    
    // Get element to determine if it's a shape
    const elementIdToShow = this.selectedIds.length === 1 ? this.selectedIds[0] : null;
   
    if (!elementIdToShow) return;
    const element = this.elements.find(el => el.id === elementIdToShow);
    if (!element) return;
    
    const isShape = isShapeElement(element);
   
    
    // Remove existing icon and preview
    if (iconGroup) {
      iconGroup.destroy();
      anchorState.iconGroup = null;
    }
    if (previewGroup) {
      previewGroup.destroy();
      anchorState.previewGroup = null;
    }
    
    // Get viewport scale for sizing
    const viewportScale = this.getViewportScale();
    const scaledAnchorRadius = this.options.anchorRadius / viewportScale;
    const scaledExpandedRadius = ANCHOR_EXPANDED_RADIUS / viewportScale;
    
    // Update circle and add icon based on state
    if (state === 'default') {
      // Default state: small blue circle
      circle.radius(scaledAnchorRadius);
      circle.fill(this.options.anchorFill);
      circle.stroke('transparent');
      circle.strokeWidth(0);
      // Force redraw to update hit area
      circle.draw();
   
    } else if (state === 'near') {
      // State 1 (non-shape) or State 3 (shape): Empty circle with colored icon
      circle.radius(scaledExpandedRadius);
      circle.fill('transparent');
      circle.stroke(ANCHOR_BLUE_COLOR);
      circle.strokeWidth(2 / viewportScale); // Scale stroke width too
      // Force redraw to update hit area for expanded circle
      circle.draw();
     
      
      // Add icon (group is already positioned at anchor.x, anchor.y)
      const icon = isShape 
        ? this.createArrowIcon(ANCHOR_BLUE_COLOR, anchor.side as 'top' | 'right' | 'bottom' | 'left')
        : this.createPlusIcon(ANCHOR_BLUE_COLOR);
      icon.position({ x: 0, y: 0 }); // Relative to group position
      anchorState.iconGroup = icon;
      group.add(icon);
    } else if (state === 'hover') {
      // State 2 (non-shape) or State 4 (shape): Filled circle with white icon + preview
      circle.radius(scaledExpandedRadius);
      circle.fill(ANCHOR_BLUE_COLOR);
      circle.stroke('transparent');
      circle.strokeWidth(0);
      // Force redraw to update hit area
      circle.draw();
     
      
      // Add white icon (group is already positioned at anchor.x, anchor.y)
      const icon = isShape 
        ? this.createArrowIcon('#FFFFFF', anchor.side as 'top' | 'right' | 'bottom' | 'left')
        : this.createPlusIcon('#FFFFFF');
      icon.position({ x: 0, y: 0 }); // Relative to group position
      anchorState.iconGroup = icon;
      group.add(icon);
      
      // Add preview
      if (isShape) {
        this.renderConnectorPreview(anchor, element, group);
      } else {
        this.renderDuplicationPreview(anchor, element, group);
      }
    }
    
    // Animate radius change for smooth transition
    const targetRadius = state === 'default' ? scaledAnchorRadius : scaledExpandedRadius;
    if (Math.abs(circle.radius() - targetRadius) > 0.1 / viewportScale) {
      const anim = new Konva.Tween({
        node: circle,
        radius: targetRadius,
        duration: 0.2,
        easing: Konva.Easings.EaseOut,
      });
      anim.play();
    }
  }

  /**
   * Render duplication preview (for non-shape elements)
   */
  private renderDuplicationPreview(anchor: AnchorPoint, element: EditableElement, anchorGroup: Konva.Group): void {
    // Preview should be added to the main anchor group (not individual anchor group) at absolute layer coordinates
    if (!this.anchorGroup) return;

    const previewGroup = new Konva.Group({ opacity: 0, listening: false });

    // Calculate preview position (50px offset for non-shape elements) in absolute layer coordinates
    const offset = 50;
    let previewX: number;
    let previewY: number;
    
    switch (anchor.side) {
      case 'right':
        previewX = anchor.x + offset;
        previewY = anchor.y - element.height / 2;
        break;
      case 'left':
        previewX = anchor.x - offset - element.width;
        previewY = anchor.y - element.height / 2;
        break;
      case 'bottom':
        previewX = anchor.x - element.width / 2;
        previewY = anchor.y + offset;
        break;
      case 'top':
        previewX = anchor.x - element.width / 2;
        previewY = anchor.y - offset - element.height;
        break;
      default:
        previewX = anchor.x + offset;
        previewY = anchor.y - element.height / 2;
    }
    
    // Create semi-transparent rectangle preview
    const previewRect = new Konva.Rect({
      x: previewX,
      y: previewY,
      width: element.width,
      height: element.height,
      fill: element.data?.backgroundColor || '#FFFFFF',
      opacity: 1, // Full opacity, will be controlled by group
      cornerRadius: element.data?.cornerRadius || 0,
      stroke: '#999999',
      strokeWidth: 1.5,
      dash: [5, 5],
      listening: false,
    });
    
    previewGroup.add(previewRect);
    
    // Position preview group at (0, 0) since elements use absolute coordinates
    previewGroup.position({ x: 0, y: 0 });
    this.anchorGroup.add(previewGroup);
    
    // Animate preview appearance
    const anim = new Konva.Tween({
      node: previewGroup,
      opacity: 0.5, // Increased visibility
      duration: 0.2,
      easing: Konva.Easings.EaseOut,
    });
    anim.play();
    
    // Store reference
    const anchorKey = this.getAnchorKey(anchor.side, element.id);
    const anchorState = this.anchorStates.get(anchorKey);
    if (anchorState) {
      anchorState.previewGroup = previewGroup;
    }
  }

  /**
   * Render connector preview (for shape elements)
   */
  private renderConnectorPreview(anchor: AnchorPoint, element: EditableElement, anchorGroup: Konva.Group): void {
    // Preview should be added to the main anchor group (not individual anchor group) at absolute layer coordinates
    if (!this.anchorGroup) return;
    
    const previewGroup = new Konva.Group({ opacity: 0, listening: false });
    
    // Calculate preview shape position (100px offset) in absolute layer coordinates
    const offset = 100;
    let previewX: number;
    let previewY: number;
    
    switch (anchor.side) {
      case 'right':
        previewX = anchor.x + offset;
        previewY = anchor.y - element.height / 2;
        break;
      case 'left':
        previewX = anchor.x - offset - element.width;
        previewY = anchor.y - element.height / 2;
        break;
      case 'bottom':
        previewX = anchor.x - element.width / 2;
        previewY = anchor.y + offset;
        break;
      case 'top':
        previewX = anchor.x - element.width / 2;
        previewY = anchor.y - offset - element.height;
        break;
      default:
        previewX = anchor.x + offset;
        previewY = anchor.y - element.height / 2;
    }
    
    // Calculate center of preview shape for connector endpoint
    const previewCenterX = previewX + element.width / 2;
    const previewCenterY = previewY + element.height / 2;
    
    // Create connector line preview (from anchor to preview shape center)
    const connectorLine = new Konva.Line({
      points: [anchor.x, anchor.y, previewCenterX, previewCenterY],
      stroke: '#999999',
      strokeWidth: 2,
      opacity: 1, // Full opacity, will be controlled by group
      dash: [5, 5],
      listening: false,
    });
    
    // Create shape preview
    const shapePreview = new Konva.Rect({
      x: previewX,
      y: previewY,
      width: element.width,
      height: element.height,
      fill: element.data?.fill || element.data?.backgroundColor || '#FFFFFF',
      opacity: 1, // Full opacity, will be controlled by group
      cornerRadius: element.data?.cornerRadius || 0,
      stroke: '#999999',
      strokeWidth: 1.5,
      dash: [5, 5],
      listening: false,
    });
    
    previewGroup.add(connectorLine);
    previewGroup.add(shapePreview);
    
    // Position preview group at (0, 0) since elements use absolute coordinates
    previewGroup.position({ x: 0, y: 0 });
    this.anchorGroup.add(previewGroup);
    
    // Animate preview appearance
    const anim = new Konva.Tween({
      node: previewGroup,
      opacity: 0.5, // Increased visibility
      duration: 0.2,
      easing: Konva.Easings.EaseOut,
    });
    anim.play();
    
    // Store reference
    const anchorKey = this.getAnchorKey(anchor.side, element.id);
    const anchorState = this.anchorStates.get(anchorKey);
    if (anchorState) {
      anchorState.previewGroup = previewGroup;
    }
  }

  /**
   * Update anchor point visualization
   * Shows anchors for selected elements (not just hovered)
   */
  private updateAnchors(): void {
    // Setup mouse tracking if not already set up (stage should be available now)
    if (!this.mouseMoveHandler) {
      const stage = this.layer.getStage();
   
      if (stage) {
        this.setupMouseTracking();
      }
    }
    
    // Clear existing anchor states
    this.anchorStates.clear();
    
    // Remove existing anchor group
    if (this.anchorGroup) {
      this.anchorGroup.destroy();
      this.anchorGroup = null;
    }

    // Don't show anchors during drag/transform operations
    if (this.isTransforming) {
      this.layer.batchDraw();
      return;
    }

    // Show anchors for selected elements (single selection only to avoid clutter)
    const elementIdToShow = this.selectedIds.length === 1 ? this.selectedIds[0] : null;
    
    if (!elementIdToShow) {
      this.layer.batchDraw();
      return;
    }

    // Find the selected element
    const element = this.elements.find(el => el.id === elementIdToShow);
    // Don't show anchors for connectors or images
    if (!element || element.type === 'connector' || element.type === 'image') {
      this.layer.batchDraw();
      return;
    }

    // Hide anchors when zoomed out too much (below threshold)
    const viewportScale = this.getViewportScale();
    const MIN_SCALE_TO_SHOW_ANCHORS = 0.45; // Hide anchors when zoomed out below 30%
    if (viewportScale < MIN_SCALE_TO_SHOW_ANCHORS) {
      this.layer.batchDraw();
      return;
    }

    // Create anchor group
    this.anchorGroup = new Konva.Group({
      name: 'anchor-group',
    });

    // Calculate anchor points
    const anchors = calculateAnchorPoints(element, this.options.anchorOffset);
    
    // Create anchor circles
    anchors.forEach((anchor) => {
      const anchorPoint: AnchorPoint = {
        side: anchor.side,
        x: anchor.x,
        y: anchor.y,
        isPrecise: false,
      };

      const group = new Konva.Group({
        x: anchor.x,
        y: anchor.y,
      });

      // Scale anchor circle radius based on viewport scale
      const viewportScale = this.getViewportScale();
      const scaledAnchorRadius = this.options.anchorRadius / viewportScale;
      
      const circle = new Konva.Circle({
        x: 0,
        y: 0,
        radius: scaledAnchorRadius,
        fill: this.options.anchorFill,
        stroke: this.options.anchorStroke,
        strokeWidth: this.options.anchorStrokeWidth / viewportScale,
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        shadowBlur: 2 / viewportScale,
        shadowOffset: { x: 0, y: 1 / viewportScale },
        shadowOpacity: 0.3,
        listening: true,
        draggable: !!this.onAnchorDragStart,
      });

      // Mouse enter/leave handlers for direct hover detection
      circle.on('mouseenter', () => {
        const anchorKey = this.getAnchorKey(anchor.side, element.id);
        const anchorState = this.anchorStates.get(anchorKey);
        if (anchorState && anchorState.state !== 'hover') {
          anchorState.state = 'hover';
          this.updateAnchorVisualState(anchorState, 'hover');
        }
      });

      circle.on('mouseleave', () => {
        // When mouse leaves anchor, check if still within 25px proximity
        // This will be handled by stage mousemove, but we can reset to default if mouse is far
        if (this.currentMousePos) {
          const distance = this.distance(this.currentMousePos.x, this.currentMousePos.y, anchor.x, anchor.y);
          const anchorKey = this.getAnchorKey(anchor.side, element.id);
          const anchorState = this.anchorStates.get(anchorKey);
          if (anchorState) {
            if (distance > HOVER_PROXIMITY_THRESHOLD) {
              // Mouse is far, reset to default
              anchorState.state = 'default';
              this.updateAnchorVisualState(anchorState, 'default');
            } else {
              // Mouse is still near, show near state
              anchorState.state = 'near';
              this.updateAnchorVisualState(anchorState, 'near');
            }
          }
        }
      });

      // Click handler
      circle.on('click tap', (e: any) => {
        e.cancelBubble = true;
        if (this.onAnchorClick) {
          this.onAnchorClick(anchorPoint, element);
        }
      });

      // Drag handlers
      if (this.onAnchorDragStart) {
        circle.on('dragstart', () => {
          if (this.onAnchorDragStart) {
            this.onAnchorDragStart(anchorPoint, element);
          }
        });

        circle.on('dragmove', (e: any) => {
          const stage = e.target.getStage();
          if (!stage) return;

          const pointerPos = stage.getPointerPosition();
          if (!pointerPos) return;

          // Convert screen coordinates to layer coordinates
          const transform = stage.getAbsoluteTransform().copy().invert();
          const layerPos = transform.point(pointerPos);

          if (this.onAnchorDragMove) {
            this.onAnchorDragMove(anchorPoint, element, layerPos);
          }
        });

        circle.on('dragend', (e: any) => {
          const stage = e.target.getStage();
          if (!stage) return;

          const pointerPos = stage.getPointerPosition();
          if (!pointerPos) return;

          // Convert screen coordinates to layer coordinates
          const transform = stage.getAbsoluteTransform().copy().invert();
          const layerPos = transform.point(pointerPos);

          if (this.onAnchorDragEnd) {
            this.onAnchorDragEnd(anchorPoint, element, layerPos);
          }
        });
      }

      group.add(circle);
      this.anchorGroup!.add(group);
      
      // Store anchor state
      const anchorKey = this.getAnchorKey(anchor.side, element.id);
      this.anchorStates.set(anchorKey, {
        anchor: anchorPoint,
        group,
        circle,
        iconGroup: null,
        state: 'default',
        previewGroup: null,
      });
   
    });

    // Add anchor group to layer (on top)
    this.layer.add(this.anchorGroup);
    this.anchorGroup.moveToTop();
    
    // Check current mouse position immediately if available
    const stage = this.layer.getStage();
    if (stage) {
      const pointerPos = stage.getPointerPosition();
      if (pointerPos) {
        // Convert to layer coordinates using layer's absolute transform (accounts for viewport zoom/pan)
        const transform = this.layer.getAbsoluteTransform().copy();
        transform.invert();
        const layerPos = transform.point(pointerPos);
        this.currentMousePos = layerPos;
        this.updateAnchorStates();
      }
    }
    
    this.layer.batchDraw();
  }

  /**
   * Hide all anchors
   */
  hideAnchors(): void {
    this.hoveredElementId = null;
    this.isTransforming = true; // Set transforming flag to prevent anchors from showing
    this.updateAnchors();
  }

  /**
   * Cleanup
   */
  destroy(): void {
    const stage = this.layer.getStage();
    if (stage) {
      if (this.mouseMoveHandler) {
        stage.off('mousemove', this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }
      
      if (this.mouseLeaveHandler) {
        stage.off('mouseleave', this.mouseLeaveHandler);
        this.mouseLeaveHandler = null;
      }
    }
    
    if (this.anchorGroup) {
      this.anchorGroup.destroy();
      this.anchorGroup = null;
    }
    
    this.anchorStates.clear();
    this.onAnchorClick = null;
    this.onAnchorDragStart = null;
    this.onAnchorDragMove = null;
    this.onAnchorDragEnd = null;
  }
}
